import { action, computed, observable } from 'mobx';
import { BaseBlock } from '~/models/blocks/base-block.model';
import {
    Currency,
    IChargeBlockResponse,
    TPaymentMethod,
    AutoPayStatus
} from '~/types/server.types';
import type { RootStore } from '~/stores/root.store';
import type { FlowRevision } from '~/models/flow-revision.model';
import { TGratuityTypes } from '~/types/model.types';

type ChargeBlockDataForServer = ReturnType<BaseBlock['getDataForServer']> &
    Pick<
        IChargeBlockResponse,
        | 'payment_method_types'
        | 'allow_autopay'
        | 'allow_gratuity'
        | 'gratuity_type'
        | 'type'
    >;

export class ChargeBlock extends BaseBlock {
    type: 'charge_block';
    @observable private _payment_method_types: TPaymentMethod[];
    @observable allow_autopay: boolean;
    @observable allow_gratuity: boolean;
    @observable gratuity_type: TGratuityTypes;

    constructor(
        block: IChargeBlockResponse,
        flowRevision: FlowRevision<RootStore>,
        rootStore: RootStore
    ) {
        super(block, flowRevision, rootStore);
        this.type = block.type;
    }

    set payment_method_types(value: TPaymentMethod[]) {
        this._payment_method_types = value;
    }

    @computed
    get payment_method_types(): TPaymentMethod[] {
        if (this.flow?.isLeadForm) {
            return ['credit_card'];
        }
        return (
            this._payment_method_types ||
            (this.flow?.currency === Currency.USD
                ? ['bank_account', 'credit_card']
                : ['credit_card'])
        );
    }

    isOnlyAllowedPaymentMethod(pm: TPaymentMethod) {
        return (
            this.payment_method_types.length === 1 &&
            this.payment_method_types[0] === pm
        );
    }

    dndAcceptedTypes() {
        return null;
    }

    get isDeletable() {
        return false;
    }

    @computed
    get shouldExpandOnSelect() {
        return false;
    }

    @computed
    get isAutoPayActive() {
        return (
            this.connectedVariable?.value?.auto_payment?.status ===
            AutoPayStatus.Active
        );
    }

    @computed
    get autoPayStatus() {
        return this.connectedVariable?.value?.auto_payment?.status;
    }

    @computed
    get isAutoPayCanceledByWorker() {
        const autoPayment = this.connectedVariable?.value?.auto_payment;
        return (
            autoPayment &&
            autoPayment.status === AutoPayStatus.Inactive &&
            autoPayment.canceled?.canceled_by === 'AutoPaymentWorker'
        );
    }

    getDataForServer(): ChargeBlockDataForServer {
        return {
            ...super.getDataForServer(),
            type: this.type as 'charge_block',
            payment_method_types: this.payment_method_types,
            allow_autopay: this.allow_autopay,
            allow_gratuity: this.allow_gratuity,
            gratuity_type: this.gratuity_type
        };
    }

    @action
    mergeBlockAttributes(block, attributes) {
        super.mergeBlockAttributes(block, attributes);
        if (block.payment_method_types) {
            this._payment_method_types = block.payment_method_types;
        }
    }

    @computed
    get gratuityType() {
        return this.gratuity_type || 'all_payments';
    }
}
